import {
  ArrowRight,
  Check,
  CheckCircle2,
  Clock3,
  Database,
  FileCheck2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Brand } from '../components/Brand';

const benefits = [
  {
    icon: Clock3,
    title: 'Tempo restituito alle persone',
    description: 'Talos assorbe i passaggi ripetitivi della preparazione, così il tempo può tornare all’analisi e alle decisioni.',
  },
  {
    icon: CheckCircle2,
    title: 'Un processo sempre coerente',
    description: 'Le stesse regole vengono applicate a ogni ciclo, senza formule sparse, copie manuali o passaggi dimenticati.',
  },
  {
    icon: ShieldCheck,
    title: 'Un risultato verificabile',
    description: 'Dati, controlli ed elaborazioni restano leggibili: il report arriva pronto, senza rinunciare alla supervisione.',
  },
];

const steps = [
  {
    number: '01',
    title: 'I dati arrivano',
    description: 'Talos acquisisce le nuove sorgenti e le collega allo storico già disponibile.',
  },
  {
    number: '02',
    title: 'Il lavoro viene eseguito',
    description: 'Normalizzazione, classificazioni e regole di business vengono applicate automaticamente.',
  },
  {
    number: '03',
    title: 'Il report è pronto',
    description: 'Tabelle, confronti e indicatori aspettano solo la verifica finale nel workspace.',
  },
];

export function LandingPage() {
  return (
    <div className="landing-page">
      <header className="landing-header">
        <Link to="/" aria-label="Talos, pagina iniziale">
          <Brand />
        </Link>
        <nav aria-label="Navigazione principale">
          <a href="#perche-talos">Perché Talos</a>
          <a href="#come-funziona">Come funziona</a>
          <Link className="landing-login-link" to="/accesso">
            Accedi <ArrowRight size={16} />
          </Link>
        </nav>
      </header>

      <main>
        <section className="landing-hero" aria-labelledby="landing-title">
          <div className="landing-hero-grid" aria-hidden="true" />
          <div className="landing-orbit landing-orbit-one" aria-hidden="true" />
          <div className="landing-orbit landing-orbit-two" aria-hidden="true" />

          <div className="landing-hero-copy">
            <p className="landing-kicker"><span /> Report automation by Justbit</p>
            <h1 id="landing-title">I tuoi report, pronti prima ancora di doverli preparare.</h1>
            <p className="landing-hero-intro">
              Talos integra i dati che arrivano, applica le regole del processo e costruisce ogni report in modo automatico, ripetibile e controllabile.
            </p>
            <div className="landing-hero-actions">
              <a className="button button-primary landing-primary-action" href="#come-funziona">
                Scopri come funziona <ArrowRight size={17} />
              </a>
              <Link className="landing-text-link" to="/accesso">Accedi al workspace</Link>
            </div>
            <div className="landing-trust-row" aria-label="Caratteristiche principali">
              <span><Check size={15} /> Integrazione automatica</span>
              <span><Check size={15} /> Regole condivise</span>
              <span><Check size={15} /> Verifica umana</span>
            </div>
          </div>

          <div className="landing-pipeline" aria-label="Dal dato ricevuto al report pronto">
            <div className="pipeline-topline">
              <span className="pipeline-live"><i /> Flusso attivo</span>
              <small>SETTEMBRE 2026</small>
            </div>

            <div className="pipeline-source-card">
              <span className="pipeline-card-icon"><Database size={18} /></span>
              <div>
                <small>NUOVA SORGENTE</small>
                <strong>Dati ricevuti</strong>
              </div>
              <span className="pipeline-status"><Check size={12} /> acquisiti</span>
            </div>

            <div className="pipeline-connector"><span /><RefreshCw size={15} /><span /></div>

            <div className="pipeline-processing-card">
              <div className="pipeline-processing-head">
                <span className="pipeline-card-icon"><Workflow size={18} /></span>
                <div><small>ELABORAZIONE</small><strong>Integrazione automatica</strong></div>
                <span>100%</span>
              </div>
              <div className="pipeline-progress"><i /></div>
              <p><Sparkles size={14} /> Storico, regole e classificazioni applicati</p>
            </div>

            <div className="pipeline-report-card">
              <div className="pipeline-report-title">
                <span className="pipeline-card-icon"><FileCheck2 size={18} /></span>
                <div><small>OUTPUT</small><strong>Report pronto</strong></div>
                <span className="pipeline-ready"><CheckCircle2 size={14} /> Verificabile</span>
              </div>
              <div className="pipeline-report-preview" aria-hidden="true">
                <span className="wide" /><span /><span className="mid" /><span /><span className="short" />
              </div>
            </div>
          </div>
        </section>

        <section className="landing-problem" id="perche-talos" aria-labelledby="problem-title">
          <div className="landing-section-heading">
            <p className="landing-kicker dark"><span /> Il problema</p>
            <h2 id="problem-title">Un report manuale non è solo lento.<br />È lavoro che ricomincia ogni volta.</h2>
          </div>
          <div className="landing-problem-grid">
            <article>
              <span>01</span>
              <h3>Dati da ricomporre</h3>
              <p>File nuovi, storico e fonti diverse devono essere ritrovati, allineati e messi nella giusta relazione.</p>
            </article>
            <article>
              <span>02</span>
              <h3>Passaggi da ripetere</h3>
              <p>Filtri, formule, classificazioni e confronti vengono replicati a mano a ogni nuovo periodo.</p>
            </article>
            <article>
              <span>03</span>
              <h3>Tempo sottratto all’analisi</h3>
              <p>L’attenzione finisce sulla preparazione del dato, invece che sulla comprensione di ciò che racconta.</p>
            </article>
          </div>
        </section>

        <section className="landing-solution" aria-labelledby="solution-title">
          <div className="landing-solution-copy">
            <p className="landing-kicker"><span /> La soluzione</p>
            <h2 id="solution-title">Talos prende in carico il processo, non solo il file.</h2>
            <p>
              Quando arrivano nuovi dati, Talos li integra con lo storico, applica le regole condivise e prepara un output coerente. Il lavoro manuale lascia spazio a un controllo finale più rapido e consapevole.
            </p>
            <ul>
              <li><CheckCircle2 size={17} /> Acquisizione e integrazione delle sorgenti</li>
              <li><CheckCircle2 size={17} /> Elaborazioni deterministiche e ripetibili</li>
              <li><CheckCircle2 size={17} /> Report già strutturati per la verifica</li>
            </ul>
          </div>
          <div className="landing-solution-quote">
            <Sparkles size={22} />
            <blockquote>“I tuoi report, pronti prima ancora di doverli preparare.”</blockquote>
            <p>La promessa di Talos</p>
          </div>
        </section>

        <section className="landing-benefits" aria-labelledby="benefits-title">
          <div className="landing-section-heading compact">
            <p className="landing-kicker dark"><span /> I vantaggi</p>
            <h2 id="benefits-title">Meno preparazione.<br />Più valore nel risultato.</h2>
          </div>
          <div className="landing-benefits-grid">
            {benefits.map(({ icon: Icon, title, description }) => (
              <article key={title}>
                <span><Icon size={21} /></span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-workflow" id="come-funziona" aria-labelledby="workflow-title">
          <div className="landing-workflow-heading">
            <p className="landing-kicker"><span /> Come funziona</p>
            <h2 id="workflow-title">Dal dato in ingresso al report pronto.</h2>
            <p>Un flusso continuo, leggibile e sempre sotto controllo.</p>
          </div>
          <div className="landing-workflow-steps">
            {steps.map((step, index) => (
              <article key={step.number}>
                <span>{step.number}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {index < steps.length - 1 && <i aria-hidden="true"><ArrowRight size={17} /></i>}
              </article>
            ))}
          </div>
        </section>

        <section className="landing-cta" aria-labelledby="cta-title">
          <div>
            <p className="landing-kicker"><span /> Workspace riservato</p>
            <h2 id="cta-title">Il prossimo report potrebbe essere già pronto.</h2>
            <p>Accedi a Talos per controllare le sorgenti, seguire il processo e verificare il risultato.</p>
          </div>
          <Link className="button button-primary" to="/accesso">
            Accedi a Talos <ArrowRight size={17} />
          </Link>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-footer-brand">
          <Brand inverse />
          <p>Report automation by Justbit</p>
        </div>
        <div className="landing-footer-story">
          <p className="landing-kicker"><span /> Perché Talos</p>
          <p>
            Nella mitologia greca, Talos era l’automa di bronzo che svolgeva senza sosta il proprio compito. Il nostro Talos fa lo stesso con il lavoro ripetitivo della reportistica: riceve i dati, esegue il processo e consegna un risultato pronto da verificare.
          </p>
        </div>
        <p className="landing-footer-legal">© {new Date().getFullYear()} Justbit · Uso interno</p>
      </footer>
    </div>
  );
}
